import { ReactNode } from "react";

export const newsContent: Record<string, ReactNode> = {
  "turo-variable-host-share-march-2026": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">
        Turo's March 31 Updates: What the New "Variable Host Share" Means for Your Bottom Line
      </h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        Effective March 31, 2026
      </p>

      <p>
        Turo recently announced a wave of changes taking effect on March 31, 2026. If you've been feeling overwhelmed trying to decipher the updates in the{" "}
        <a href="https://help.turo.com/new-and-upcoming-on-turo-Hyt3_dZja" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
          Turo Help Center
        </a>
        , you are not alone.
      </p>

      <p className="mt-4">
        Turo is overhauling how earnings work in several major US markets, updating its terminology, and completely replacing its protection plans in <strong>Australia</strong>, <strong>Canada</strong>, the <strong>UK</strong>, and the <strong>US</strong>.
      </p>

      <p className="mt-4">
        Here is a plain-English breakdown of exactly what is changing, followed by actionable strategies you can use to protect your margins and keep your business thriving.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Part 1: The News (What is Changing)</h2>

      <h3 className="text-lg font-semibold mt-8">1. The US Market: Variable Host Shares</h3>
      <p>
        The biggest news is the introduction of a <strong>Variable Host Share</strong> for hosts in select US markets: <strong>Austin</strong>, <strong>Dallas</strong>, <strong>Detroit</strong>, <strong>Las Vegas</strong>, <strong>Maui</strong>, <strong>Philadelphia</strong>, <strong>Phoenix</strong>, <strong>San Diego</strong>, and <strong>Seattle</strong>.
      </p>

      <p className="mt-4">
        If your cars are located in these cities, your cut of the earnings will now depend on how far in advance a guest books their trip.
      </p>

      <p className="mt-4">
        Turo is rewarding advance bookings and penalizing last-minute ones. Here is how your cut changes based on the booking window:
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 font-semibold">Booking Window</th>
              <th className="text-left py-3 px-4 font-semibold">Your Host Share</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">Booked 28+ days in advance</td>
              <td className="py-3 px-4">Maximum cut (80% to 100%, depending on plan)</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">Booked 14–27 days in advance</td>
              <td className="py-3 px-4">Slightly lower cut (75% to 95%)</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">Booked 3–13 days in advance</td>
              <td className="py-3 px-4">Lower cut (70% to 90%)</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">Booked 0–2 days in advance (Last-Minute)</td>
              <td className="py-3 px-4">Lowest cut (65% to 85%)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-sm text-muted-foreground italic">
        Note: This variable rate applies to the trip price, extra mileage fees, and late return fees. Once a trip is booked, that percentage is locked in, even if the guest modifies the trip later.
      </p>

      <p className="mt-4">
        <strong>Terminology Update:</strong> Across the US, Turo is also changing how it talks about insurance. The term "deductible" is being replaced with "damage responsibility," and your "host take rate" is now simply called your "host share."
      </p>

      <hr className="my-8" />

      <h3 className="text-lg font-semibold mt-8">2. Standardized Earnings Plans</h3>
      <p>
        For hosts across the <strong>US</strong>, <strong>Australia</strong>, <strong>Canada</strong>, and the <strong>UK</strong>, the old percentage-based protection plans are being reorganized. They are generally being replaced by three cleanly named tiers:
      </p>
      <ol className="list-decimal pl-6 space-y-2 my-4">
        <li>
          <strong>More earnings plan:</strong> You keep the highest percentage of revenue, but pay the highest out-of-pocket costs if a car is damaged.
        </li>
        <li>
          <strong>Balanced plan:</strong> A middle-ground option.
        </li>
        <li>
          <strong>More peace of mind plan:</strong> You keep the lowest percentage of revenue, but have the lowest out-of-pocket costs for damage.
        </li>
      </ol>
      <p>
        On March 31, Turo will automatically move your vehicles into one of these new plans based on what you are currently using.
      </p>

      <hr className="my-8" />

      <h3 className="text-lg font-semibold mt-8">3. The Data: Old vs. New Plan Charts</h3>
      <p>
        If you just want the bottom-line strategies, feel free to skip to Part 2. If you want the exact numbers on how the plans are migrating in your specific market, use the reference tables below.
      </p>

      <h4 className="font-semibold mt-6 mb-3">United States Migration</h4>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 font-semibold">Old Plan</th>
              <th className="text-left py-3 px-4 font-semibold">New Plan</th>
              <th className="text-left py-3 px-4 font-semibold">New Host Share</th>
              <th className="text-left py-3 px-4 font-semibold">New Damage Responsibility</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">90 plan</td>
              <td className="py-3 px-4">More earnings plan</td>
              <td className="py-3 px-4">90%</td>
              <td className="py-3 px-4">$2,750</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">85 plan / 80 plan</td>
              <td className="py-3 px-4">Balanced plan</td>
              <td className="py-3 px-4">80%</td>
              <td className="py-3 px-4">$1,500</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">75 plan / 60 plan</td>
              <td className="py-3 px-4">More peace of mind plan</td>
              <td className="py-3 px-4">70%</td>
              <td className="py-3 px-4">$250</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 className="font-semibold mt-6 mb-3">Australia Migration</h4>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 font-semibold">Old Plan</th>
              <th className="text-left py-3 px-4 font-semibold">New Plan</th>
              <th className="text-left py-3 px-4 font-semibold">New Host Share</th>
              <th className="text-left py-3 px-4 font-semibold">New Damage Responsibility</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">90%</td>
              <td className="py-3 px-4">More earnings plan</td>
              <td className="py-3 px-4">85%</td>
              <td className="py-3 px-4">$2,500 AUD</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 className="font-semibold mt-6 mb-3">Canada Migration</h4>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 font-semibold">Old Plan</th>
              <th className="text-left py-3 px-4 font-semibold">New Plan</th>
              <th className="text-left py-3 px-4 font-semibold">New Host Share</th>
              <th className="text-left py-3 px-4 font-semibold">New Damage Responsibility</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">85%</td>
              <td className="py-3 px-4">More earnings plan</td>
              <td className="py-3 px-4">85%</td>
              <td className="py-3 px-4">$2,000 CAD</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">(New Option)</td>
              <td className="py-3 px-4">Balanced plan</td>
              <td className="py-3 px-4">75%</td>
              <td className="py-3 px-4">$250 CAD</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">75%</td>
              <td className="py-3 px-4">More peace of mind plan</td>
              <td className="py-3 px-4">65%</td>
              <td className="py-3 px-4">$0 CAD</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 className="font-semibold mt-6 mb-3">United Kingdom Migration</h4>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 font-semibold">Old Plan</th>
              <th className="text-left py-3 px-4 font-semibold">New Plan</th>
              <th className="text-left py-3 px-4 font-semibold">New Host Share</th>
              <th className="text-left py-3 px-4 font-semibold">Out-of-Pocket Costs</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">(New Option)</td>
              <td className="py-3 px-4">More earnings plan</td>
              <td className="py-3 px-4">80%</td>
              <td className="py-3 px-4">£1,750</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">75%</td>
              <td className="py-3 px-4">Balanced plan</td>
              <td className="py-3 px-4">70%</td>
              <td className="py-3 px-4">£250</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-3 px-4">65%</td>
              <td className="py-3 px-4">More peace of mind plan</td>
              <td className="py-3 px-4">60%</td>
              <td className="py-3 px-4">£0</td>
            </tr>
          </tbody>
        </table>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Part 2: The Strategy (How to Deal With It)</h2>
      <p>
        When platform rules change, successful hosts don't panic — they adapt. Here is how you can adjust your strategy to make these updates work for you.
      </p>

      <h3 className="text-lg font-semibold mt-8">Strategies for US Hosts in "Variable" Markets</h3>
      <p>
        If you are in one of the cities where Turo is taking a bigger cut for last-minute trips, your goal is simple: <strong>Encourage early bookings, and protect your margins on late ones.</strong>
      </p>
      <ul className="list-disc pl-6 space-y-4 my-4">
        <li>
          <strong>Leverage the "Non-Refundable" Discount:</strong> Turo no longer has an "Early Bird" discount feature. The best tool you have to encourage guests to book ahead of time is the Non-Refundable Discount. By offering a 10% or 15% discount for non-refundable trips booked 4 or more days in advance, you incentivize guests to commit early. This pushes more of your bookings into those higher-earning timeframes where Turo takes less out of your pocket.
        </li>
        <li>
          <strong>Raise Base Prices for Last-Minute Days:</strong> Because Turo is going to take a larger slice of your pie for trips booked 0 to 2 days in advance, you should offset that "penalty." You can do this by manually raising your daily prices on your calendar for the immediate upcoming 48 hours. If a guest really needs a car at the last minute, they will pay the premium, which makes up for your lower host share.
        </li>
      </ul>

      <h3 className="text-lg font-semibold mt-8">Advanced Plan Selection Strategies (All Markets)</h3>
      <p>
        Your priority right now is making sure you don't get defaulted into a plan that hurts your business model. <strong>Log in and check your default migration immediately</strong> so Turo doesn't guess what is best for your business.
      </p>
      <p>
        When choosing your new plan, be careful with simplified "rules of thumb." Your ideal plan depends heavily on your specific fleet economics:
      </p>

      <h4 className="font-semibold mt-6">The Luxury Vehicle Trap</h4>
      <p>
        It might seem logical to put a high-end luxury vehicle on the "More peace of mind" plan to avoid a massive repair bill. However, giving up an extra 10% to 20% of your earnings on a high-grossing vehicle could literally mean leaving thousands of dollars on the table every single month. In many cases, the math dictates that you are better off taking the "More earnings" plan and putting that extra revenue into a dedicated maintenance/damage fund, rather than paying Turo a massive premium for a lower deductible.
      </p>

      <h4 className="font-semibold mt-6">The Fleet Size Factor</h4>
      <p>
        Your risk tolerance should scale with your fleet. If you only have one or two economy cars, a $2,500 out-of-pocket expense could wipe out your entire season's profit. In that scenario, a middle-tier "Balanced" plan makes sense. But if you manage a fleet of 15+ vehicles, you can self-insure. Across a large fleet, the statistical probability of major accidents is spread out, making the highest-risk/highest-reward "More earnings" plan the most profitable choice in the long run.
      </p>

      <h4 className="font-semibold mt-6">Run Your Own Numbers</h4>
      <p>
        Don't guess. Look at your historical damage claims over the last 12 months. Compare what you paid out-of-pocket versus what you would have lost in revenue by taking a 10% lower host share. Choose the path that leaves the most cash in your bank account at the end of the year.
      </p>

      <hr className="my-8" />

      <p className="text-muted-foreground italic">
        Have questions about optimizing your pricing strategy to handle these changes? The HostCaptain team is always here to help you navigate the math and keep your fleet profitable.
      </p>

      <hr className="my-8" />

      <p className="text-center">
        <a href="/" className="text-primary underline hover:text-primary/80 font-medium">
          ← Back to HostCaptain
        </a>
      </p>
    </div>
  ),
};
